import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  isNilOrZero,
} from '@populateui/utils';
import {
  FormWorkflowCompletionRequestDTO,
  FormWorkflowInitiateRequestDTO,
  FormWorkflowInitiationRequirementsDTO,
  FormWorkflowInitiationRequirementsRequestDTO,
  FormWorkflowPatientFormStatusResponseDTO,
  FormWorkflowWithTransitionSuccessMessageResponseDTO,
  FormsRenderingMetadataResponseDTO,
  None,
} from 'dtos';
import {
  FormFieldAssignee,
} from 'dtos/formFieldAssignee';
import {
  getApiFormWorkflowGetFormStatusByPatientPatientId,
  getApiFormWorkflowGetRenderingMetadataFormWorkflowIdAssignee,
  postApiFormWorkflowCompleteClinicianStep,
  postApiFormWorkflowGetInitiationRequirements,
  postApiFormWorkflowInitiate,
  postApiFormWorkflowSaveClinicianFieldValues,
} from 'endpoints';

export function usePatientFormStatuses(
  patientId?: number,
  enabled: boolean = true,
): UseQueryResult<FormWorkflowPatientFormStatusResponseDTO, Error> {
  return useQuery<FormWorkflowPatientFormStatusResponseDTO, Error>({
    queryKey: ['form-workflow-status', patientId],
    queryFn: () => getApiFormWorkflowGetFormStatusByPatientPatientId(patientId ?? 0),
    enabled: enabled && !isNilOrZero(patientId),
  });
}

export function useStartFormWorkflowRequirements():
  UseMutationResult<
    FormWorkflowInitiationRequirementsDTO,
    Error,
    FormWorkflowInitiationRequirementsRequestDTO
    > {
  return useMutation({
    mutationFn: (request) => postApiFormWorkflowGetInitiationRequirements(request),
  });
}

export function useStartFormWorkflow():
  UseMutationResult<
    FormWorkflowWithTransitionSuccessMessageResponseDTO,
    Error,
    FormWorkflowInitiateRequestDTO
    > {
  return useMutation({
    mutationFn: (request) => postApiFormWorkflowInitiate(request),
  });
}

export function useClinicianFormRenderingMetadata(
  formWorkflowId?: number,
  assignee: FormFieldAssignee = FormFieldAssignee.Clinician,
  enabled: boolean = true,
): UseQueryResult<FormsRenderingMetadataResponseDTO, Error> {
  return useQuery<FormsRenderingMetadataResponseDTO, Error>({
    queryKey: ['form-workflow-rendering', formWorkflowId, assignee],
    queryFn: () => getApiFormWorkflowGetRenderingMetadataFormWorkflowIdAssignee(
      formWorkflowId ?? 0,
      assignee,
    ),
    enabled: enabled && !isNilOrZero(formWorkflowId),
  });
}

export function useSaveClinicianFormFields():
  UseMutationResult<None, Error, FormWorkflowCompletionRequestDTO> {
  return useMutation({
    mutationFn: (request) => postApiFormWorkflowSaveClinicianFieldValues(request),
  });
}

export function useCompleteClinicianFormStep():
  UseMutationResult<
    FormWorkflowWithTransitionSuccessMessageResponseDTO,
    Error,
    FormWorkflowCompletionRequestDTO
    > {
  return useMutation({
    mutationFn: (request) => postApiFormWorkflowCompleteClinicianStep(request),
  });
}
